import React, { createContext, useCallback, useState, useRef, useEffect } from 'react';
import { useReducer } from 'react';
import { NotificationsReducer } from '../Reducers/NotificationReducer';

export const NotificationsContext = createContext();

const MAX_NOTIFICATIONS = 50; // Limite le nombre de notifications en mémoire
const NOTIFICATION_PAGE_SIZE = 20; // Nombre de notifications par page

const NotificationsContextProvider = (props) => {
    const [showNotificationsView, setShowNotificationsView] = useState(false);
    const [notifications, dispatchNotifications] = useReducer(NotificationsReducer, []);
    const [notifsUnseenLength, setNotifsUnseenLength] = useState(0);
    const [currentPage, setCurrentPage] = useState(1);
    const [hasMore, setHasMore] = useState(true);
    const notificationCache = useRef(new Map()); // Cache pour éviter les doublons
    const connectionState = useRef({
        isReconnecting: false,
        lastReconnectTime: 0
    });

    // Optimisation: Debounce pour la mise à jour des notifications non lues
    useEffect(() => {
        const debounceTimer = setTimeout(() => {
            if (notifications.length > 0) {
                const unseenNotifications = notifications.filter(notification => !notification.seen);
                setNotifsUnseenLength(unseenNotifications.length);
            } else {
                setNotifsUnseenLength(0);
            }
        }, 300);

        return () => clearTimeout(debounceTimer);
    }, [notifications]);

    // Fonction de gestion de reconnexion sans mettre à jour l'état complet
    const handleReconnection = useCallback(() => {
        const now = Date.now();
        
        // Limiter les reconnexions à une fois toutes les 10 secondes pour éviter les rafales
        if (now - connectionState.current.lastReconnectTime < 10000) {
            return;
        }
        
        connectionState.current.isReconnecting = true;
        connectionState.current.lastReconnectTime = now;
        
        // Après reconnexion réussie
        connectionState.current.isReconnecting = false;
    }, []);

    // Gestion optimisée de l'ajout des notifications
    const handleNewNotification = useCallback((notification) => {
        // Vérifier si nous sommes en cours de reconnexion avant d'ajouter
        if (connectionState.current.isReconnecting) {
            return; // Ignorer les nouvelles notifications pendant la reconnexion
        }
        
        // Ignorer les notifications invalides
        if (!notification || typeof notification !== 'object' || !notification.id) {
            return;
        }
        
        // Vérifier si la notification existe déjà avant de la traiter
        if (notificationCache.current.has(notification.id)) {
            return; // Éviter les doublons
        }
        
        // Marquer comme vu dans le cache
        notificationCache.current.set(notification.id, true);
        
        // Ajouter la notification
        dispatchNotifications({
            type: 'ADD_NOTIFICATION',
            payload: [notification]
        });
    }, [dispatchNotifications]);

    // Chargement paginé des anciennes notifications
    const loadMoreNotifications = useCallback(async () => {
        // Ne pas charger plus si nous sommes en cours de reconnexion
        if (connectionState.current.isReconnecting) {
            return;
        }
        
        try {
            const nextPage = currentPage + 1;
            setCurrentPage(nextPage);
        } catch (error) {
            // Silently handle errors
        }
    }, [currentPage]);

    // Optimisation: Mise à jour groupée des notifications lues
    const markNotificationsAsSeen = useCallback((notificationIds) => {
        dispatchNotifications({
            type: 'MARK_NOTIFICATIONS_SEEN',
            payload: notificationIds
        });
    }, []);

    const SwitchNotifactions = useCallback(() => {
        setShowNotificationsView(prev => !prev);
    }, []);

    // Nettoyage du cache moins fréquent et avec protection contre les reconnexions
    useEffect(() => {
        const cleanupInterval = setInterval(() => {
            // Ne pas nettoyer le cache si nous sommes en cours de reconnexion
            if (!connectionState.current.isReconnecting) {
                notificationCache.current.clear();
            }
        }, 1000 * 60 * 60 * 4); // Nettoie le cache toutes les 4 heures au lieu de chaque heure

        return () => clearInterval(cleanupInterval);
    }, []);

    return (
        <NotificationsContext.Provider
            value={{
                showNotificationsView,
                SwitchNotifactions,
                notifications,
                dispatchNotifications,
                notifsUnseenLength,
                loadMoreNotifications,
                hasMore,
                markNotificationsAsSeen,
                handleNewNotification,
                handleReconnection
            }}>
            {props.children}
        </NotificationsContext.Provider>
    );
};

export default NotificationsContextProvider;